import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
/**
 * Emisión de comprobante. Form con tipo, serie, cliente y items dinámicos.
 * Calcula totales en vivo respetando afectación IGV (18%).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { formatApiError } from "../api/client";
import {
  type ComprobanteCreatePayload,
  type ComprobanteItemInput,
  type Serie,
  type TipoAfectacionIGV,
  type TipoComprobante,
  type TipoDocCliente,
  emitirComprobante,
  listSeries,
} from "../api/facturacion";
import { type Sede, listSedes } from "../api/tenants";
import { useAuth } from "../auth/AuthProvider";
import { useBase } from "../auth/TenantBrandingProvider";
import { Alert, Button, Card, CardBody, CardHeader, Field, Input } from "../components/ui";

const TASA_IGV = 0.18;

interface ItemDraft {
  uid: number; // estable para usar como key de React (los índices cambian al reordenar/eliminar)
  descripcion: string;
  cantidad: string;
  precio_unitario: string;
  tipo_afectacion_igv: TipoAfectacionIGV;
}

let _uidSeq = 0;
function nuevoItem(): ItemDraft {
  return {
    uid: ++_uidSeq,
    descripcion: "",
    cantidad: "1",
    precio_unitario: "0.00",
    tipo_afectacion_igv: "EXONERADO",
  };
}

export function ComprobanteEmitirPage() {
  const base = useBase();
  const navigate = useNavigate();
  const { me } = useAuth();

  const [tipo, setTipo] = useState<TipoComprobante>("BOLETA");
  const [sedeId, setSedeId] = useState<number | null>(null);
  const [serieId, setSerieId] = useState<number | null>(null);

  const [tipoDoc, setTipoDoc] = useState<TipoDocCliente>("DNI");
  const [numeroDoc, setNumeroDoc] = useState("");
  const [razonSocial, setRazonSocial] = useState("");
  const [direccion, setDireccion] = useState("");
  const [email, setEmail] = useState("");

  const [items, setItems] = useState<ItemDraft[]>(() => [nuevoItem()]);
  const [error, setError] = useState<string | null>(null);

  const sedesQuery = useQuery({ queryKey: ["sedes"], queryFn: listSedes });
  const seriesQuery = useQuery({
    queryKey: ["series"],
    queryFn: () => listSeries(),
  });

  const sedes: Sede[] = sedesQuery.data ?? [];
  const series: Serie[] = seriesQuery.data ?? [];

  // Series filtradas por tipo + sede + activa
  const seriesAplicables = useMemo(
    () =>
      series.filter(
        (s) => s.tipo_comprobante === tipo && s.activa && (sedeId == null || s.sede_id === sedeId),
      ),
    [series, tipo, sedeId],
  );

  // Si cambia el tipo o sede, resetear serie seleccionada si ya no aplica
  useEffect(() => {
    if (serieId && !seriesAplicables.some((s) => s.id === serieId)) {
      setSerieId(seriesAplicables[0]?.id ?? null);
    } else if (!serieId && seriesAplicables.length > 0) {
      setSerieId(seriesAplicables[0].id);
    }
  }, [seriesAplicables, serieId]);

  // Si cambia el tipo a FACTURA y el tipoDoc no es RUC, cambiarlo
  useEffect(() => {
    if (tipo === "FACTURA" && tipoDoc !== "RUC") {
      setTipoDoc("RUC");
    }
  }, [tipo, tipoDoc]);

  // Totales en vivo
  const totales = useMemo(() => {
    let sg = 0;
    let se = 0;
    let si_ = 0;
    let ig = 0;
    for (const it of items) {
      const cantidad = Number(it.cantidad) || 0;
      const precio = Number(it.precio_unitario) || 0;
      const bruto = cantidad * precio;
      if (it.tipo_afectacion_igv === "GRAVADO") {
        sg += bruto;
        ig += bruto * TASA_IGV;
      } else if (it.tipo_afectacion_igv === "EXONERADO") {
        se += bruto;
      } else {
        si_ += bruto;
      }
    }
    return {
      subtotal_gravado: sg,
      subtotal_exonerado: se,
      subtotal_inafecto: si_,
      igv: ig,
      total: sg + se + si_ + ig,
    };
  }, [items]);

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (payload: ComprobanteCreatePayload) => emitirComprobante(payload),
    onSuccess: (c) => {
      // El listado de comprobantes se queda con la foto de antes: el que
      // acaba de emitirse no aparecería hasta que caduque la caché.
      queryClient.invalidateQueries({ queryKey: ["comprobantes"] });
      navigate(`${base}/facturacion/${c.id}`);
    },
    onError: (err) => setError(formatApiError(err, "No se pudo emitir el comprobante.")),
  });

  if (!me?.permisos.includes("comprobante:create")) {
    return <Navigate to={`${base}/facturacion`} replace />;
  }

  function setItem(i: number, patch: Partial<ItemDraft>) {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  }

  function addItem() {
    setItems((prev) => [...prev, nuevoItem()]);
  }

  function removeItem(i: number) {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!sedeId) return setError("Seleccioná una sede.");
    if (!serieId) return setError("Seleccioná una serie.");
    if (items.length === 0) return setError("Agregá al menos un ítem.");
    for (const it of items) {
      if (!it.descripcion.trim()) return setError("Falta descripción en algún ítem.");
      if (Number(it.cantidad) <= 0) return setError("Cantidad inválida en algún ítem.");
    }

    const payload: ComprobanteCreatePayload = {
      tipo_comprobante: tipo,
      serie_id: serieId,
      sede_id: sedeId,
      cliente: {
        tipo_doc: tipoDoc,
        numero_doc: tipoDoc === "SIN_DOC" ? null : numeroDoc.trim() || null,
        razon_social: razonSocial.trim(),
        direccion: direccion.trim() || null,
        email: email.trim() || null,
      },
      items: items.map(
        (it): ComprobanteItemInput => ({
          descripcion: it.descripcion.trim(),
          cantidad: it.cantidad,
          precio_unitario: it.precio_unitario,
          tipo_afectacion_igv: it.tipo_afectacion_igv,
        }),
      ),
    };
    mutation.mutate(payload);
  }

  return (
    <div className="space-y-6">
      <Link
        to={`${base}/facturacion`}
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver a facturación
      </Link>

      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Emitir comprobante</h1>
        <p className="text-sm text-slate-500 mt-1">
          La emisión envía el comprobante al OSE configurado. Una vez aceptado, su número SUNAT no
          se puede cambiar.
        </p>
      </div>

      {error && <Alert>{error}</Alert>}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* --- Datos del comprobante --- */}
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold text-slate-900">Datos del comprobante</h2>
          </CardHeader>
          <CardBody className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Tipo" htmlFor="tipo">
              <select
                id="tipo"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                value={tipo}
                onChange={(e) => setTipo(e.target.value as TipoComprobante)}
              >
                <option value="BOLETA">Boleta</option>
                <option value="FACTURA">Factura</option>
              </select>
            </Field>
            <Field label="Sede" htmlFor="sede">
              <select
                id="sede"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                value={sedeId ?? ""}
                onChange={(e) => setSedeId(Number(e.target.value) || null)}
              >
                <option value="">— Seleccionar —</option>
                {sedes.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Serie"
              htmlFor="serie"
              hint={
                seriesAplicables.length === 0
                  ? `Sin series ${tipo === "BOLETA" ? "B###" : "F###"} activas para esa sede.`
                  : undefined
              }
            >
              <select
                id="serie"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                value={serieId ?? ""}
                onChange={(e) => setSerieId(Number(e.target.value) || null)}
              >
                <option value="">— Seleccionar —</option>
                {seriesAplicables.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.serie} (siguiente: {s.correlativo_actual + 1})
                  </option>
                ))}
              </select>
            </Field>
          </CardBody>
        </Card>

        {/* --- Cliente --- */}
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold text-slate-900">Cliente</h2>
          </CardHeader>
          <CardBody className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Tipo de documento" htmlFor="tipoDoc">
              <select
                id="tipoDoc"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                value={tipoDoc}
                onChange={(e) => setTipoDoc(e.target.value as TipoDocCliente)}
                disabled={tipo === "FACTURA"}
              >
                <option value="DNI">DNI</option>
                <option value="RUC">RUC</option>
                <option value="CE">Carnet de extranjería</option>
                <option value="PASAPORTE">Pasaporte</option>
                <option value="SIN_DOC">Sin documento (boleta)</option>
              </select>
            </Field>
            <Field
              label="Número de documento"
              htmlFor="numeroDoc"
              hint={
                tipoDoc === "DNI"
                  ? "8 dígitos"
                  : tipoDoc === "RUC"
                    ? "11 dígitos"
                    : tipoDoc === "SIN_DOC"
                      ? "No aplica"
                      : undefined
              }
            >
              <Input
                id="numeroDoc"
                value={numeroDoc}
                onChange={(e) => setNumeroDoc(e.target.value)}
                disabled={tipoDoc === "SIN_DOC"}
              />
            </Field>
            <Field label="Razón social / Nombre" htmlFor="razonSocial">
              <Input
                id="razonSocial"
                value={razonSocial}
                onChange={(e) => setRazonSocial(e.target.value)}
                required
              />
            </Field>
            <Field label="Dirección" htmlFor="direccion">
              <Input
                id="direccion"
                value={direccion}
                onChange={(e) => setDireccion(e.target.value)}
              />
            </Field>
            <Field label="Email (opcional)" htmlFor="email">
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
          </CardBody>
        </Card>

        {/* --- Items --- */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900">Ítems</h2>
              <Button type="button" variant="ghost" onClick={addItem} className="text-xs">
                <Plus className="w-3.5 h-3.5" />
                Agregar ítem
              </Button>
            </div>
          </CardHeader>
          <CardBody className="space-y-3">
            {items.map((it, i) => {
              const bruto = (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0);
              const igv = it.tipo_afectacion_igv === "GRAVADO" ? bruto * TASA_IGV : 0;
              const total = bruto + igv;
              return (
                <div
                  key={it.uid}
                  className="grid grid-cols-12 gap-2 items-end p-3 border border-slate-200 rounded-lg"
                >
                  <div className="col-span-12 md:col-span-4">
                    <Field label="Descripción" htmlFor={`desc-${i}`}>
                      <Input
                        id={`desc-${i}`}
                        value={it.descripcion}
                        onChange={(e) => setItem(i, { descripcion: e.target.value })}
                        required
                      />
                    </Field>
                  </div>
                  <div className="col-span-4 md:col-span-1">
                    <Field label="Cant." htmlFor={`cant-${i}`}>
                      <Input
                        id={`cant-${i}`}
                        type="number"
                        min="0"
                        step="0.0001"
                        value={it.cantidad}
                        onChange={(e) => setItem(i, { cantidad: e.target.value })}
                      />
                    </Field>
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <Field label="P. unit." htmlFor={`pu-${i}`}>
                      <Input
                        id={`pu-${i}`}
                        type="number"
                        min="0"
                        step="0.0001"
                        value={it.precio_unitario}
                        onChange={(e) => setItem(i, { precio_unitario: e.target.value })}
                      />
                    </Field>
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <Field label="IGV" htmlFor={`igv-${i}`}>
                      <select
                        id={`igv-${i}`}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={it.tipo_afectacion_igv}
                        onChange={(e) =>
                          setItem(i, {
                            tipo_afectacion_igv: e.target.value as TipoAfectacionIGV,
                          })
                        }
                      >
                        <option value="EXONERADO">Exonerado</option>
                        <option value="GRAVADO">Gravado 18%</option>
                        <option value="INAFECTO">Inafecto</option>
                      </select>
                    </Field>
                  </div>
                  <div className="col-span-10 md:col-span-2 text-right">
                    <div className="text-xs text-slate-500">Total</div>
                    <div className="font-medium text-slate-900">S/ {total.toFixed(2)}</div>
                  </div>
                  <div className="col-span-2 md:col-span-1 flex justify-end">
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(i)}
                        className="text-slate-400 hover:text-red-600 p-2"
                        aria-label="Quitar ítem"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </CardBody>
        </Card>

        {/* --- Totales + submit --- */}
        <Card>
          <CardBody className="flex items-end justify-between gap-6 flex-wrap">
            <div className="text-sm text-slate-700 space-y-1 min-w-[240px]">
              <div className="flex justify-between gap-8">
                <span>Gravado</span>
                <span>S/ {totales.subtotal_gravado.toFixed(2)}</span>
              </div>
              <div className="flex justify-between gap-8">
                <span>Exonerado</span>
                <span>S/ {totales.subtotal_exonerado.toFixed(2)}</span>
              </div>
              <div className="flex justify-between gap-8">
                <span>Inafecto</span>
                <span>S/ {totales.subtotal_inafecto.toFixed(2)}</span>
              </div>
              <div className="flex justify-between gap-8">
                <span>IGV (18%)</span>
                <span>S/ {totales.igv.toFixed(2)}</span>
              </div>
              <div className="flex justify-between gap-8 pt-1 border-t border-slate-200 font-semibold text-slate-900">
                <span>Total</span>
                <span>S/ {totales.total.toFixed(2)}</span>
              </div>
            </div>
            <Button type="submit" loading={mutation.isPending}>
              Emitir comprobante
            </Button>
          </CardBody>
        </Card>
      </form>
    </div>
  );
}
